/**
 * Rendering server instants in the EMPLOYEE's timezone, never the viewer's browser zone.
 *
 * Punch instants are UTC (CLAUDE.md invariant 3); "what time did they clock in" only has an answer
 * relative to the location the punch happened at. `toLocaleTimeString` with no `timeZone` option
 * answers it relative to whoever is looking — so an HR user in Bengaluru reading a New York
 * employee's ledger saw every punch shifted by 10.5 hours, and the same row disagreed with the
 * Status Report screen, which resolves times server-side against `Location.timezone`.
 */

/** `hh:mm AM` in `timeZone`, or an em dash for a missing/unparseable instant. */
export function formatTimeInZone(isoInstant: string | null | undefined, timeZone: string): string {
  if (!isoInstant) return '—';
  const date = new Date(isoInstant);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone,
    });
  } catch {
    // An unknown IANA zone throws a RangeError rather than degrading; the UTC reading is wrong by
    // an offset but still readable, which beats blanking the column.
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    });
  }
}

/** The viewer's own IANA zone: the honest fallback while data with the employee's zone is still loading, rather than
 * assuming every viewer is in India (P3-2). */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/**
 * "morning" / "afternoon" / "evening" for a greeting, by the viewer's own clock (or `timeZone`). A greeting is about
 * the person looking at the screen, not a working day, so the browser's zone is the right default. Evening runs
 * through the night, until 05:00.
 */
export function timeOfDayGreeting(now: Date = new Date(), timeZone?: string): 'morning' | 'afternoon' | 'evening' {
  let hour: number;
  try {
    hour = Number(
      new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', ...(timeZone ? { timeZone } : {}) }).format(now),
    );
  } catch {
    hour = now.getHours();
  }
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  return 'evening';
}

/**
 * The calendar date (`YYYY-MM-DD`) of `instant` in `timeZone`, or in the browser's own zone when none is given
 * (P3-2). Never the UTC date: `new Date().toISOString().slice(0, 10)` is still "yesterday" until 05:30 in India, so a
 * form defaulting to it offered the wrong day for the first five and a half hours of every morning.
 */
export function localDateKey(instant: Date = new Date(), timeZone?: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timeZone ? { timeZone } : {}),
    }).format(instant);
  } catch {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
  }
}

/**
 * This month up to today (`YYYY-MM-DD`, the browser's own calendar — see `localDateKey`). The default
 * range of the attendance admin screens. They used to open on a fixed 1–13 September 2026 left in from
 * development, so in October every company saw "0" until someone changed the dates.
 */
export function monthToDateRange(now: Date = new Date()): { startDate: string; endDate: string } {
  const today = localDateKey(now);
  return { startDate: `${today.slice(0, 7)}-01`, endDate: today };
}

/**
 * `YYYY-MM-DD` of a Date built from local calendar parts (`new Date(y, m, d)`, or a local Date moved with
 * `setDate`). `toISOString()` on such a Date shifts it to UTC and lands on the previous day east of UTC (P3-2).
 */
export function dateKeyOf(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Elapsed time since an open clock-in. Zone-independent: a duration is the same everywhere. */
export function formatElapsed(sinceIso: string | null | undefined, now: Date): string | null {
  if (!sinceIso) return null;
  const since = new Date(sinceIso).getTime();
  if (Number.isNaN(since)) return null;
  const diffMinutes = Math.floor((now.getTime() - since) / 60000);
  if (diffMinutes < 0) return null;
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

/** Detailed live elapsed time including seconds: `04h 23m 15s` or `00h 05m 30s` */
export function formatElapsedWithSeconds(
  sinceIso: string | null | undefined,
  now: Date,
  deductMinutes = 0,
): string | null {
  if (!sinceIso) return null;
  const since = new Date(sinceIso).getTime();
  if (Number.isNaN(since)) return null;
  const grossMs = now.getTime() - since;
  if (grossMs < 0) return null;
  const diffMs = Math.max(0, grossMs - Math.max(0, deductMinutes) * 60_000);
  const totalSecs = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSecs / 3600);
  const minutes = Math.floor((totalSecs % 3600) / 60);
  const seconds = totalSecs % 60;
  return `${String(hours).padStart(2, '0')}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
}

/**
 * `2m ago` / `3h ago` / `Yesterday` / `12 Mar` — a short, human relative label for a UI element
 * like the notification bell, where the exact instant matters far less than "is this new".
 *
 * Zone-independent, like `formatElapsed` above: this is elapsed wall-clock time since an instant,
 * not a "what day is it" resolution (CLAUDE.md invariant 3 governs the latter — shift boundaries,
 * attendance windows, leave years — none of which this is).
 */
export function formatRelativeTime(isoInstant: string | null | undefined, now: Date): string {
  if (!isoInstant) return '';
  const then = new Date(isoInstant).getTime();
  if (Number.isNaN(then)) return '';
  const diffSeconds = Math.max(0, Math.floor((now.getTime() - then) / 1000));

  if (diffSeconds < 60) return 'Just now';
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(isoInstant).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** `+1h 30m` / `-45m` / `0h 00m` — one consistent hours format, signed when asked. */
export function formatHoursLabel(hours: number, signed = false): string {
  const sign = hours < 0 ? '-' : signed && hours > 0 ? '+' : '';
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${sign}${h}h ${String(m).padStart(2, '0')}m`;
}

/**
 * `Sep 17, 2026` for a date-only (`YYYY-MM-DD`) or instant string — the one human-readable date
 * format this module should render everywhere, instead of each screen picking its own
 * `toLocaleDateString()` options (or, worse, printing the raw ISO string unformatted).
 */
export function formatDateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: value.length <= 10 ? 'UTC' : undefined,
  });
}
